import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import implementationChecklistService from '../services/implementationChecklistService';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { handleAIError } from '../utils/aiErrorHandler';

const normalize = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const UNASSIGNED_VALUES = ['Non affecté', 'Not assigned', 'Unassigned'];

const isUnassignedValue = (value) => {
  if (!String(value ?? '').trim()) return true;
  return UNASSIGNED_VALUES.some((unassigned) => normalize(unassigned) === normalize(value));
};

const resolveResponsible = (raw, notAssignedValue) => (
  isUnassignedValue(raw) ? notAssignedValue : String(raw).trim()
);

const measureResponsible = (measure) => (
  measure?.responsiblePerson
  || measure?.responsibleParty
  || measure?.responsible
  || measure?.owner
  || ''
);

const measureId = (measure) => {
  const rawId = measure?.id ?? measure?._id;
  if (rawId === undefined || rawId === null) return '';
  const value = typeof rawId === 'object' ? rawId.id ?? rawId._id ?? rawId : rawId;
  return String(value).trim();
};

const measureDisplayName = (measure) => (
  measure?.name || measure?.title || measure?.measureName || measure?.description || ''
);

const normalizeStatus = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const EXCLUDED_MEASURE_STATUSES = new Set([
  'abandon',
  'abandoned',
  'abandoned_measure',
  'abandoned_fr',
  'abandonned',
  'abandonne',
  'abandonnee',
  'abandoned_plan',
  'not_planned',
  'not_planned_fr',
  'not_planned_decision',
  'non_planifie',
  'non_planifie_fr',
  'non_planifiee',
  'non_planifiee_fr',
  'non_planifies',
  'non_planifies_fr',
  'non_planified',
]);

const isExcludedStatus = (value) => EXCLUDED_MEASURE_STATUSES.has(normalizeStatus(value));

const isFalseFlag = (value) => (
  value === false
  || value === 0
  || (typeof value === 'string' && value.trim().toLowerCase() === 'false')
);

const getMeasureStatusValues = (measure) => [
  measure?.status,
  measure?.implementation,
  measure?.decision,
  measure?.implementationStatus,
  measure?.decisionStatus,
]
  .filter((status) => status !== undefined && status !== null && String(status).trim() !== '')
  .map(normalizeStatus);

/**
 * The measures table is the source of truth for checklist generation.  A
 * measure remains actionable when its status is not explicitly abandoned or
 * not planned; missing status is treated as planned for legacy rows.
 */
export const isActionableMeasure = (measure) => {
  if (!measure || typeof measure !== 'object' || Array.isArray(measure)) return false;
  if (isFalseFlag(measure.retained) || !measureId(measure)) return false;

  return !getMeasureStatusValues(measure).some(isExcludedStatus);
};

const getUniqueActionableMeasures = (measures) => {
  const seen = new Set();
  return (Array.isArray(measures) ? measures : []).filter((measure) => {
    if (!isActionableMeasure(measure)) return false;
    const id = measureId(measure);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const getMeasureStatus = (measure) => {
  const statuses = getMeasureStatusValues(measure);
  const excludedStatus = statuses.find(isExcludedStatus);
  if (excludedStatus) return excludedStatus;

  if (statuses.some((status) => [
    'implemented',
    'apply_before',
    'completed',
    'complete',
    'done',
    'realized',
    'realised',
  ].includes(status))) {
    return 'implemented';
  }

  if (statuses.some((status) => [
    'in_progress',
    'inprogress',
    'progressing',
    'started',
  ].includes(status))) {
    return 'in_progress';
  }

  return statuses[0] || 'planned';
};

/**
 * Backend questions use one scalar measure association.  The legacy array is
 * accepted only when it contains one item so existing saved checklists remain
 * readable while new data is always represented by concerned_measure_id.
 */
export const getQuestionMeasureId = (question) => {
  const singularId = question?.concerned_measure_id;
  if (singularId !== undefined && singularId !== null) {
    const value = typeof singularId === 'object'
      ? singularId.id ?? singularId._id
      : singularId;
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  const legacyIds = question?.concerned_measure_ids;
  if (Array.isArray(legacyIds) && legacyIds.length === 1) {
    const legacyId = legacyIds[0];
    if (legacyId !== undefined && legacyId !== null && String(legacyId).trim() !== '') {
      return String(legacyId).trim();
    }
  }

  return null;
};

const getQuestionMeasure = (question, measures) => {
  const questionId = getQuestionMeasureId(question);
  if (!questionId) return null;
  return (Array.isArray(measures) ? measures : [])
    .find((measure) => measureId(measure) === questionId) || null;
};

const getQuestionMeasureNames = (question, measures) => {
  const measure = getQuestionMeasure(question, measures);
  if (measure) {
    const name = measureDisplayName(measure);
    return name ? [name] : [];
  }

  if (Array.isArray(question?.concerned_measures)) {
    return question.concerned_measures
      .filter((name) => name !== undefined && name !== null && String(name).trim() !== '')
      .slice(0, 1)
      .map((name) => String(name).trim());
  }

  return question?.concerned_measures
    ? [String(question.concerned_measures).trim()]
    : [];
};

const normalizeResponse = (response) => {
  if (response === 'yes' || response === 'no' || response === 'na') return response;
  return null;
};

export const normalizeChecklistCategory = (category, language = 'fr') => {
  const normalized = normalizeStatus(category);
  const english = /^en\b/i.test(String(language || ''));
  const categories = {
    technique: english ? 'Technical' : 'Technique',
    technical: english ? 'Technical' : 'Technique',
    organisationnel: english ? 'Organizational' : 'Organisationnel',
    organizational: english ? 'Organizational' : 'Organisationnel',
    humain: english ? 'Human' : 'Humain',
    human: english ? 'Human' : 'Humain',
    documentation: 'Documentation',
  };
  return categories[normalized] || (english ? 'Technical' : 'Technique');
};

/**
 * Convert API questions to the frontend's flat, single-measure shape. The
 * measures argument is deliberately required for display-name and filtering;
 * no backend measure snapshot is used as a substitute.
 */
export const normalizeChecklistQuestion = (
  question,
  measures = [],
  { forceUnanswered = false, requireMeasure = false, language = 'fr' } = {}
) => {
  if (!question || typeof question !== 'object' || Array.isArray(question)) return null;
  if (typeof question.question !== 'string' || !question.question.trim()) return null;

  const concernedMeasureId = getQuestionMeasureId(question);
  if (!concernedMeasureId) return null;
  if (requireMeasure && !getQuestionMeasure({ concerned_measure_id: concernedMeasureId }, measures)) {
    return null;
  }

  const normalized = {
    ...question,
    concerned_measure_id: concernedMeasureId,
    concerned_measures: getQuestionMeasureNames(question, measures),
    category: normalizeChecklistCategory(question.category, language),
    response: forceUnanswered ? null : normalizeResponse(question.response),
    justification: forceUnanswered ? '' : (question.justification || ''),
  };

  delete normalized.concerned_measure_ids;
  return normalized;
};

export const normalizeChecklistQuestions = (questions, measures = [], options = {}) => (
  (Array.isArray(questions) ? questions : [])
    .map((question) => normalizeChecklistQuestion(question, measures, options))
    .filter(Boolean)
);

const getChecklistFallbackTemplates = (measure, language) => {
  const english = /^en\b/i.test(String(language || ''));
  const status = getMeasureStatus(measure);
  if (english) {
    return [
      {
        question: 'Before starting, are ownership, resources, and the conditions needed by the measure clearly defined?',
        category: 'Organizational',
      },
      {
        question: status === 'implemented'
          ? 'Has implementation of the measure been completed in line with the agreed decisions?'
          : status === 'in_progress'
            ? 'Is implementation of the measure progressing in line with the agreed decisions?'
            : 'Has implementation of the measure been started in line with the agreed decisions?',
        category: 'Human',
      },
      {
        question: 'Have the results of the measure been reviewed and validated with supporting evidence?',
        category: 'Documentation',
      },
    ];
  }

  return [
    {
      question: 'Avant de commencer, les responsabilités, les ressources et les conditions nécessaires à la mesure concernée sont-elles clairement définies ?',
      category: 'Organisationnel',
    },
    {
      question: status === 'implemented'
        ? 'La mise en œuvre de la mesure concernée a-t-elle été réalisée conformément aux décisions prises ?'
        : status === 'in_progress'
          ? 'La mise en œuvre de la mesure concernée progresse-t-elle conformément aux décisions prises ?'
          : 'La mise en œuvre de la mesure concernée a-t-elle été engagée conformément aux décisions prises ?',
      category: 'Humain',
    },
    {
      question: 'Les résultats de la mesure concernée ont-ils été contrôlés et validés à l’aide d’éléments justificatifs ?',
      category: 'Documentation',
    },
  ];
};

const makeChecklistQuestionId = (measure, index, language) => {
  const safeId = String(measureId(measure) || 'measure').replace(/[^a-z0-9_-]/gi, '_');
  return `w5_${language}_${safeId}_${index + 1}`;
};

/**
 * Keep the generated API boundary deterministic even when an older service
 * returns an incomplete batch. Valid AI questions are retained; missing rows
 * are filled with the same three localized, closed-ended templates.
 */
export const normalizeGeneratedChecklistQuestions = (
  questions,
  measures = [],
  language = 'fr'
) => {
  const actionableMeasures = getUniqueActionableMeasures(measures);
  if (actionableMeasures.length === 0) return [];

  const normalized = normalizeChecklistQuestions(
    questions,
    actionableMeasures,
    { requireMeasure: true, forceUnanswered: true, language }
  );
  const byMeasure = new Map(actionableMeasures.map((measure) => [measureId(measure), []]));
  const usedIds = new Set();

  normalized.forEach((question) => {
    const id = String(question.concerned_measure_id);
    if (byMeasure.has(id)) byMeasure.get(id).push(question);
  });

  const result = [];
  actionableMeasures.forEach((measure) => {
    const candidates = byMeasure.get(measureId(measure)) || [];
    const templates = getChecklistFallbackTemplates(measure, language);
    for (let index = 0; index < templates.length; index += 1) {
      const source = candidates[index];
      const fallback = templates[index];
      let question = source
        ? { ...source }
        : {
            id: makeChecklistQuestionId(measure, index, language),
            question: fallback.question,
            category: fallback.category,
            concerned_measure_id: measureId(measure),
            concerned_measures: [measureDisplayName(measure)],
            response: null,
            justification: '',
          };
      if (!question.id || usedIds.has(String(question.id))) {
        const baseId = makeChecklistQuestionId(measure, index, language);
        let uniqueId = baseId;
        let suffix = 2;
        while (usedIds.has(uniqueId)) {
          uniqueId = `${baseId}_${suffix}`;
          suffix += 1;
        }
        question = { ...question, id: uniqueId };
      }
      usedIds.add(String(question.id));
      result.push(question);
    }
  });

  return result;
};

const getCurrentVersionData = (responsible) => {
  if (!responsible) return null;
  if (responsible.currentVersionData && (
    Array.isArray(responsible.currentVersionData.responses)
    || (responsible.currentVersionData.responses && typeof responsible.currentVersionData.responses === 'object')
    || responsible.currentVersionData.status !== undefined
    || responsible.currentVersionData.versionNumber !== undefined
  )) {
    return responsible.currentVersionData;
  }
  const versions = Array.isArray(responsible.versions) ? responsible.versions : [];
  return versions.find(
    (version) => String(version.versionNumber) === String(responsible.currentVersion || 1)
  )
    || versions[versions.length - 1]
    || responsible.currentVersionData
    || null;
};

const getSavedResponses = (responsible) => {
  const versionData = getCurrentVersionData(responsible);
  if (Array.isArray(versionData?.responses)) return versionData.responses;
  if (Array.isArray(responsible?.responses)) return responsible.responses;
  if (versionData?.responses && typeof versionData.responses === 'object') {
    return Object.entries(versionData.responses).map(([questionId, value]) => (
      value && typeof value === 'object'
        ? { questionId, ...value }
        : { questionId, response: value }
    ));
  }
  return [];
};

const updateResponsibleResponse = (responsible, questionId, patch) => {
  const versionData = getCurrentVersionData(responsible) || {
    versionNumber: responsible?.currentVersion || 1,
    responses: [],
  };
  const existing = getSavedResponses(responsible);
  const responseIndex = existing.findIndex(
    (response) => String(response.questionId) === String(questionId)
  );
  const responses = responseIndex >= 0
    ? existing.map((response, index) => (
      index === responseIndex ? { ...response, ...patch } : response
    ))
    : [...existing, { questionId, ...patch }];

  const nextVersionData = { ...versionData, responses };
  const nextVersions = Array.isArray(responsible?.versions)
    ? responsible.versions.map((version) => (
      String(version.versionNumber) === String(versionData.versionNumber)
        ? nextVersionData
        : version
    ))
    : responsible?.versions;

  return {
    ...responsible,
    ...(responsible?.currentVersionData ? { currentVersionData: nextVersionData } : {}),
    versions: nextVersions,
    currentVersionData: nextVersionData,
  };
};

export const getChecklistQuestionStats = (questions = []) => {
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const yes = safeQuestions.filter((question) => question?.response === 'yes').length;
  const no = safeQuestions.filter((question) => question?.response === 'no').length;
  const answered = yes + no;
  const total = safeQuestions.length;

  return {
    total,
    answered,
    yes,
    no,
    completionRate: total > 0 ? Math.round((answered / total) * 100) : 0,
  };
};

const useImplementationChecklistStore = create(
  subscribeWithSelector((set, get) => ({
    checklistData: null,
    checklistQuestions: [],
    checklistHistory: [],
    checklistGenerated: false,
    approvalStatus: null,
    versionInfo: {
      currentVersion: 1,
      isLocked: false,
      status: 'draft',
      allVersions: [],
      rejectionReason: null
    },
    currentVersionIndex: null,
    isOnBranch: false,
    isLoading: false,
    isGenerating: false,
    isSaving: false,
    isDeleting: false,
    generationProgress: 0,
    error: null,
    analysisId: null,

    resetState: () => {
      set({
        checklistData: null,
        checklistQuestions: [],
        checklistHistory: [],
        checklistGenerated: false,
        approvalStatus: null,
        versionInfo: {
          currentVersion: 1,
          isLocked: false,
          status: 'draft',
          allVersions: [],
          rejectionReason: null
        },
        currentVersionIndex: null,
        isOnBranch: false,
        isLoading: false,
        isGenerating: false,
        isSaving: false,
        isDeleting: false,
        generationProgress: 0,
        error: null,
        analysisId: null
      });
    },

    loadGlobalChecklist: async (analysisId) => {
      set({ isLoading: true, error: null, analysisId });
      try {
        const result = await implementationChecklistService.getGlobalUnified(analysisId);
        if (!result.success) {
          set({ isLoading: false, error: result.error || 'Erreur de chargement' });
          return { success: false, error: result.error };
        }
        set({ checklistData: result.data, isLoading: false });
        return { success: true, data: result.data };
      } catch (error) {
        console.error('[useImplementationChecklistStore] loadGlobalChecklist error:', error);
        set({ isLoading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    deleteChecklist: async (analysisId) => {
      const targetId = analysisId || get().analysisId;
      if (!targetId) {
        const error = 'No analysisId';
        set({ isDeleting: false, error });
        toast.error(i18n.t('implementationChecklist.deleteChecklist.error'));
        return { success: false, error };
      }

      set({ isDeleting: true, error: null });
      try {
        const result = await implementationChecklistService.deleteChecklist(targetId);
        if (!result.success) {
          throw new Error(result.error || 'Delete failed');
        }

        const deleted = result.data?.deleted !== false;
        set({
          checklistData: null,
          checklistQuestions: [],
          checklistHistory: [],
          checklistGenerated: false,
          approvalStatus: null,
          versionInfo: {
            currentVersion: 1,
            isLocked: false,
            status: 'draft',
            allVersions: [],
            rejectionReason: null
          },
          currentVersionIndex: null,
          isOnBranch: false,
          generationProgress: 0,
          isDeleting: false,
          error: null
        });
        toast.success(
          i18n.t(
            deleted
              ? 'implementationChecklist.deleteChecklist.success'
              : 'implementationChecklist.deleteChecklist.nothingToDelete'
          )
        );
        return { success: true, deleted, data: result.data };
      } catch (error) {
        const message = error.message || 'Delete failed';
        console.error('[useImplementationChecklistStore] deleteChecklist error:', error);
        set({ isDeleting: false, error: message });
        toast.error(i18n.t('implementationChecklist.deleteChecklist.error'));
        return { success: false, error: message };
      }
    },

    generateChecklist: async ({ analysisId, language = 'fr', allMeasures: allMeasuresOverride } = {}) => {
      const { checklistData } = get();
      const targetId = analysisId || get().analysisId;
      if (!targetId) {
        toast.error(i18n.t('common.error') || 'Erreur');
        return { success: false };
      }

      set({ isGenerating: true, generationProgress: 0, error: null });

      // The Atelier 5 measures prop is the sole source of measure rows. The
      // backend filters again, but doing it here keeps regeneration and the
      // local question cache deterministic.
      const allMeasures = getUniqueActionableMeasures(allMeasuresOverride);
      const languageCode = /^en\b/i.test(String(language || '')) ? 'en' : 'fr';
      const scenariosCount = checklistData?.scenarios?.length || 0;
      let progressInterval;

      progressInterval = setInterval(() => {
        set((state) => {
          if (state.generationProgress >= 100) return state;
          return { generationProgress: state.generationProgress + 1 };
        });
      }, 100);

      try {
        const result = await implementationChecklistService.generateQuestions(targetId, {
          globalGeneration: true,
          allMeasures,
          scenariosCount,
          language: languageCode,
        });

        const generatedQuestions = result?.data?.questions || result?.data?.data?.questions;
        if (!result?.success || !Array.isArray(generatedQuestions)) {
          throw new Error(result?.error || 'Generation failed');
        }

        // Older API responses used a one-item concerned_measure_ids array.
        // Normalize at this boundary and always start a new generation blank.
        const newQuestions = normalizeGeneratedChecklistQuestions(
          generatedQuestions,
          allMeasures,
          languageCode
        );

        set((state) => {
          const baseChecklistData = state.checklistData || {
            measures: allMeasures,
            scenarios: [],
            checklistResponses: [],
            responsibleChecklists: []
          };
          const updatedResponses = [...(baseChecklistData.checklistResponses || [])];
          const globalIndex = updatedResponses.findIndex(
            (responseGroup) => responseGroup.scenarioId === 'global'
          );
          const existingHistory = globalIndex >= 0
            ? updatedResponses[globalIndex].checklistData?.history || []
            : [];

          const newResponse = {
            scenarioId: 'global',
            checklistData: {
              currentQuestions: newQuestions,
              history: existingHistory,
            },
          };

          if (globalIndex >= 0) {
            updatedResponses[globalIndex] = {
              ...updatedResponses[globalIndex],
              ...newResponse,
            };
          } else {
            updatedResponses.push(newResponse);
          }

          return {
            checklistQuestions: newQuestions,
            checklistGenerated: newQuestions.length > 0,
            checklistHistory: existingHistory,
            checklistData: {
              ...baseChecklistData,
              // Keep the snapshot aligned with the prop without ever using it
              // as a fallback source for deriveQuestions.
              measures: allMeasures,
              checklistResponses: updatedResponses,
              aiChecklistResponses: {
                ...(baseChecklistData.aiChecklistResponses || {}),
                questions: newQuestions,
              },
            },
            generationProgress: 100,
          };
        });

        const saveResult = await implementationChecklistService.saveResponsesGlobal(targetId, {
          responses: newQuestions,
          questions: newQuestions,
          updateCurrentVersion: true,
        });
        if (!saveResult?.success) {
          throw new Error(saveResult?.error || 'Generation save failed');
        }

        set({ isGenerating: false });
        return { success: true, questions: newQuestions };
      } catch (error) {
        console.error('[useImplementationChecklistStore] generateChecklist error:', error);
        const handled = handleAIError(error, { language: i18n.language?.startsWith('en') ? 'en' : 'fr', showNotification: false });
        const friendlyMessage = `${handled.error.userMessage.title} : ${handled.error.userMessage.message}`;
        set({ isGenerating: false, error: friendlyMessage });
        toast.error(friendlyMessage);
        return { success: false, error: friendlyMessage };
      } finally {
        clearInterval(progressInterval);
      }
    },

    deriveQuestions: (selectedResponsible, measuresOverride) => {
      const { checklistData, checklistQuestions: currentQs } = get();
      if (!checklistData) return;

      const notAssignedValue = i18n.t('implementationChecklist.notAssigned');
      // The measures argument is supplied by the Atelier 5 table. Never use
      // checklistData.measures here: it is only a persisted checklist snapshot.
      const measuresList = Array.isArray(measuresOverride) ? measuresOverride : [];
      const actionableMeasures = getUniqueActionableMeasures(measuresList);

      let rawQuestions = [];
      let history = [];
      const responseGroups = Array.isArray(checklistData.checklistResponses)
        ? checklistData.checklistResponses
        : [];
      const globalResponse = responseGroups.find(
        (responseGroup) => responseGroup.scenarioId === 'global'
      );
      const globalResponseData = globalResponse?.checklistData || globalResponse;
      if (globalResponseData) {
        rawQuestions = Array.isArray(globalResponseData.currentQuestions)
          && globalResponseData.currentQuestions.length > 0
          ? globalResponseData.currentQuestions
          : globalResponseData.questions || [];
        history = Array.isArray(globalResponseData.history) ? globalResponseData.history : [];
      }

      if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) {
        const aiPayload = checklistData.aiChecklistResponses;
        rawQuestions = Array.isArray(aiPayload)
          ? aiPayload
          : aiPayload?.questions
            || aiPayload?.data?.questions
            || [];
      }
      if (!Array.isArray(rawQuestions)) rawQuestions = [];

      // Normalize once against the prop. This removes legacy multi-measure
      // rows, unassociated rows, and rows belonging to excluded measures.
      const baseQuestions = normalizeChecklistQuestions(
        rawQuestions,
        actionableMeasures,
        { requireMeasure: true, language: i18n.language === 'en' ? 'en' : 'fr' }
      );

      const currentResponsesMap = new Map();
      (Array.isArray(currentQs) ? currentQs : []).forEach((question) => {
        const questionId = question?.id;
        if (questionId === undefined || questionId === null) return;
        if (question.response !== null && question.response !== undefined) {
          currentResponsesMap.set(String(questionId), {
            response: question.response,
            justification: question.justification || '',
          });
        }
      });

      const withDerivedResponsible = (question) => {
        if (question.responsiblePerson) return question;
        const linkedMeasure = getQuestionMeasure(question, actionableMeasures);
        return {
          ...question,
          _derivedResponsible: linkedMeasure
            ? resolveResponsible(measureResponsible(linkedMeasure), notAssignedValue)
            : notAssignedValue,
        };
      };

      const applyCurrentAndSavedResponses = (questions, savedResponses = []) => questions.map((question) => {
        const questionId = String(question.id);
        const current = currentResponsesMap.get(questionId);
        const saved = savedResponses.find(
          (response) => String(response.questionId) === questionId
        );
        if (current) {
          return { ...question, response: current.response, justification: current.justification };
        }
        if (saved) {
          return {
            ...question,
            response: normalizeResponse(saved.response),
            justification: saved.justification || '',
          };
        }
        return { ...question, response: null, justification: '' };
      });

      if (selectedResponsible) {
        const targetResponsible = normalize(selectedResponsible.name);
        const responsibleMeasures = actionableMeasures.filter(
          (measure) => normalize(
            resolveResponsible(measureResponsible(measure), notAssignedValue)
          ) === targetResponsible
        );
        const responsibleMeasureIds = new Set(responsibleMeasures.map(measureId));

        // The measure association is authoritative. An old responsible name is
        // not enough to make a question visible after the measure was excluded.
        const relevantQuestions = baseQuestions.filter((question) => (
          responsibleMeasureIds.has(String(question.concerned_measure_id))
        ));

        const responsibleEntries = Array.isArray(checklistData.responsibleChecklists)
          ? checklistData.responsibleChecklists
          : [];
        const entry = responsibleEntries.find(
          (responsible) => normalize(responsible.responsibleName) === targetResponsible
        );
        const currentVersionData = getCurrentVersionData(entry);
        const questionsWithResponses = applyCurrentAndSavedResponses(
          relevantQuestions,
          getSavedResponses(entry)
        ).map(withDerivedResponsible);

        const versionStatus = currentVersionData?.status || 'draft';
        set({
          checklistQuestions: questionsWithResponses,
          checklistHistory: history,
          checklistGenerated: baseQuestions.length > 0,
          approvalStatus: versionStatus,
          versionInfo: {
            currentVersion: entry?.currentVersion || 1,
            isLocked: currentVersionData?.isLocked || false,
            status: versionStatus,
            allVersions: entry?.versions || [],
            rejectionReason: currentVersionData?.rejectionReason || null,
          },
        });
        return;
      }

      const responsibleEntries = Array.isArray(checklistData.responsibleChecklists)
        ? checklistData.responsibleChecklists
        : [];
      const allResponses = new Map();
      responsibleEntries.forEach((responsible) => {
        getSavedResponses(responsible).forEach((response) => {
          if (response?.questionId !== undefined && response?.questionId !== null) {
            allResponses.set(String(response.questionId), response);
          }
        });
      });

      const globalQuestionsWithResponses = applyCurrentAndSavedResponses(
        baseQuestions,
        Array.from(allResponses.values())
      ).map(withDerivedResponsible);

      set({
        checklistQuestions: globalQuestionsWithResponses,
        checklistHistory: history,
        checklistGenerated: baseQuestions.length > 0,
        approvalStatus: null,
        versionInfo: {
          currentVersion: 1,
          isLocked: false,
          status: 'draft',
          allVersions: [],
          rejectionReason: null,
        },
      });
    },

    setResponse: (questionId, response, selectedResponsible) => {
      const { versionInfo } = get();
      if (versionInfo.isLocked && selectedResponsible) {
        return {
          success: false,
          locked: true,
          message: `La version ${versionInfo.currentVersion} est validée et verrouillée. Une nouvelle version sera créée lors de la prochaine sauvegarde.`
        };
      }

      set((state) => {
        const updateQuestion = (question) => (
          String(question.id) === String(questionId)
            ? normalizeChecklistQuestion(
              { ...question, response },
              [],
              { language: /^en\b/i.test(String(i18n.language || '')) ? 'en' : 'fr' }
            ) || { ...question, response }
            : question
        );
        const updatedChecklistData = state.checklistData
          ? { ...state.checklistData }
          : null;
        if (!updatedChecklistData) {
          return { checklistQuestions: state.checklistQuestions.map(updateQuestion) };
        }

        const updatedResponses = (updatedChecklistData.checklistResponses || []).map((responseGroup) => {
          if (responseGroup.scenarioId !== 'global') return responseGroup;
          return {
            ...responseGroup,
            checklistData: {
              ...responseGroup.checklistData,
              currentQuestions: (responseGroup.checklistData?.currentQuestions || []).map(updateQuestion),
            },
          };
        });
        const updatedAiQuestions = (updatedChecklistData.aiChecklistResponses?.questions || [])
          .map(updateQuestion);

        let updatedResponsibles = updatedChecklistData.responsibleChecklists || [];
        if (selectedResponsible) {
          updatedResponsibles = updatedResponsibles.map((responsible) => (
            normalize(responsible.responsibleName) === normalize(selectedResponsible.name)
              ? updateResponsibleResponse(responsible, questionId, {
                response,
                justification: state.checklistQuestions.find(
                  (question) => String(question.id) === String(questionId)
                )?.justification || '',
              })
              : responsible
          ));
        }

        return {
          checklistData: {
            ...updatedChecklistData,
            checklistResponses: updatedResponses,
            aiChecklistResponses: updatedChecklistData.aiChecklistResponses
              ? { ...updatedChecklistData.aiChecklistResponses, questions: updatedAiQuestions }
              : updatedChecklistData.aiChecklistResponses,
            responsibleChecklists: updatedResponsibles,
          },
          checklistQuestions: state.checklistQuestions.map(updateQuestion),
        };
      });

      return { success: true, locked: false };
    },

    setJustification: (questionId, justification, selectedResponsible) => {
      const { versionInfo } = get();
      if (versionInfo.isLocked && selectedResponsible) {
        return {
          success: false,
          locked: true,
          message: `La version ${versionInfo.currentVersion} est validée et verrouillée. Une nouvelle version sera créée lors de la prochaine sauvegarde.`
        };
      }

      set((state) => {
        const updateQuestion = (question) => (
          String(question.id) === String(questionId)
            ? normalizeChecklistQuestion(
              { ...question, justification },
              [],
              { language: /^en\b/i.test(String(i18n.language || '')) ? 'en' : 'fr' }
            ) || { ...question, justification }
            : question
        );
        const updatedChecklistData = state.checklistData
          ? { ...state.checklistData }
          : null;
        if (!updatedChecklistData) {
          return { checklistQuestions: state.checklistQuestions.map(updateQuestion) };
        }

        const updatedResponsibles = (updatedChecklistData.responsibleChecklists || []).map((responsible) => {
          const currentResponses = getSavedResponses(responsible);
          const hasQuestion = currentResponses.some(
            (response) => String(response.questionId) === String(questionId)
          );
          return hasQuestion
            ? updateResponsibleResponse(responsible, questionId, { justification })
            : responsible;
        });
        const updatedResponses = (updatedChecklistData.checklistResponses || []).map((responseGroup) => {
          if (responseGroup.scenarioId !== 'global') return responseGroup;
          return {
            ...responseGroup,
            checklistData: {
              ...responseGroup.checklistData,
              currentQuestions: (responseGroup.checklistData?.currentQuestions || []).map(updateQuestion),
            },
          };
        });

        return {
          checklistData: {
            ...updatedChecklistData,
            checklistResponses: updatedResponses,
            aiChecklistResponses: updatedChecklistData.aiChecklistResponses
              ? {
                  ...updatedChecklistData.aiChecklistResponses,
                  questions: (updatedChecklistData.aiChecklistResponses.questions || []).map(updateQuestion),
                }
              : updatedChecklistData.aiChecklistResponses,
            responsibleChecklists: updatedResponsibles,
          },
          checklistQuestions: state.checklistQuestions.map(updateQuestion),
        };
      });

      return { success: true, locked: false };
    },

    saveResponses: async ({
      selectedResponsible,
      status = null,
      lock = null,
      reason = null,
      forceNewVersion = false,
      updateCurrentVersion = false,
      skipReload = false
    } = {}) => {
      const { checklistQuestions, approvalStatus: currentApproval, analysisId: storedId } = get();
      const analysisId = storedId;
      if (!analysisId) return { success: false, error: 'No analysisId' };

      const questionStats = getChecklistQuestionStats(checklistQuestions);
      const questionsPayload = {
        responses: checklistQuestions,
        questions: checklistQuestions,
        totalQuestions: questionStats.total,
        answeredQuestions: questionStats.answered,
        progress: questionStats.completionRate,
        completionRate: questionStats.completionRate,
      };

      set({ isSaving: true });
      try {
        let response;
        const targetStatus = status || currentApproval || 'draft';

        if (selectedResponsible) {
          if (status === 'approved' || status === 'rejected') {
            response = await implementationChecklistService.approveResponsible(analysisId, {
              responsibleName: selectedResponsible.name,
              decision: status,
              reason
            });
          } else {
            response = await implementationChecklistService.saveResponsible(analysisId, {
              responsibleName: selectedResponsible.name,
              ...questionsPayload,
              status: targetStatus,
              proofFile: null
            });
          }
        } else if (forceNewVersion) {
          const { currentVersionIndex } = get();
          const branchFrom = currentVersionIndex !== null ? currentVersionIndex : null;
          response = await implementationChecklistService.saveResponsesGlobal(analysisId, {
            ...questionsPayload,
            branchFrom,
            updateCurrentVersion: false
          });
        } else {
          response = await implementationChecklistService.saveResponsesGlobal(analysisId, {
            ...questionsPayload,
            updateCurrentVersion
          });
        }

        if (!response.success) {
          throw new Error(response.error || 'Save failed');
        }

        if (selectedResponsible && status) {
          set((state) => ({
            approvalStatus: status,
            versionInfo: {
              ...state.versionInfo,
              status,
              isLocked: lock !== null ? lock : state.versionInfo.isLocked
            }
          }));
        }

        const branchMessage = forceNewVersion ? ' (nouvelle branche)' : '';
        let successMessage = `${i18n.t('implementationChecklist.saveSuccess') || 'Enregistré avec succès'}${branchMessage}`;
        if (status) {
          const labels = { submitted: 'soumise', approved: 'approuvée', rejected: 'rejetée', draft: 'brouillon' };
          successMessage = `Checklist ${labels[status] || status} avec succès.`;
        }
        toast.success(successMessage);

        if (!skipReload) {
          await get().loadGlobalChecklist(analysisId);
        }

        return { success: true, data: response.data };
      } catch (error) {
        console.error('[useImplementationChecklistStore] saveResponses error:', error);
        toast.error(`${i18n.t('common.error') || 'Erreur'}: ${error.message}`);
        return { success: false, error: error.message };
      } finally {
        set({ isSaving: false });
      }
    },

    submitForApproval: (selectedResponsible) =>
      get().saveResponses({ selectedResponsible, status: 'submitted', lock: true }),

    approve: (selectedResponsible) =>
      get().saveResponses({ selectedResponsible, status: 'approved', lock: true }),

    reject: (selectedResponsible, reason) =>
      get().saveResponses({ selectedResponsible, status: 'rejected', lock: false, reason }),

    unlock: (selectedResponsible) => {
      const { approvalStatus } = get();
      return get().saveResponses({ selectedResponsible, status: approvalStatus, lock: false });
    },

    createGlobalSnapshot: async () => {
      const { checklistData, analysisId } = get();
      if (!analysisId || !checklistData) return { success: false };

      set({ isSaving: true });
      try {
        const snapshotResult = await get().saveResponses({ forceNewVersion: true, skipReload: true });
        if (!snapshotResult?.success) {
          throw new Error(snapshotResult?.error || 'Snapshot save failed');
        }

        const responsibleEntries = Array.isArray(checklistData.responsibleChecklists)
          ? checklistData.responsibleChecklists
          : [];
        const approvedResponsibles = responsibleEntries.filter(
          (responsible) => getCurrentVersionData(responsible)?.status === 'approved'
        );

        for (const responsible of approvedResponsibles) {
          try {
            const currentResponses = getSavedResponses(responsible);
            await implementationChecklistService.saveResponsible(analysisId, {
              responsibleName: responsible.responsibleName,
              responses: currentResponses,
              status: 'draft',
              proofFile: null
            });
          } catch (err) {
            console.error(`[GLOBAL SNAPSHOT] Bump failed for ${responsible.responsibleName}:`, err);
          }
        }

        await get().loadGlobalChecklist(analysisId);
        toast.success(
          i18n.t('implementationChecklist.snapshotSuccess', { count: approvedResponsibles.length }) ||
            `Nouvelle version globale créée. ${approvedResponsibles.length} responsables mis à jour.`
        );
        return { success: true };
      } catch (error) {
        console.error('[useImplementationChecklistStore] createGlobalSnapshot error:', error);
        toast.error(`${i18n.t('common.error') || 'Erreur'}: ${error.message}`);
        return { success: false, error: error.message };
      } finally {
        set({ isSaving: false });
      }
    },

    addQuestion: (question = {}) => {
      const newQ = normalizeChecklistQuestion({
        ...question,
        id: question.id ?? `q-${Date.now()}`,
        category: question.category || 'Technique',
        response: null,
        justification: '',
      }, [], { language: /^en\b/i.test(String(i18n.language || '')) ? 'en' : 'fr' });

      // A question without a single measure association is not a valid flat
      // checklist row. The modal normally prevents this; keep the store safe
      // for programmatic callers as well.
      if (!newQ) return null;

      set((state) => {
        const currentQuestions = Array.isArray(state.checklistQuestions)
          ? state.checklistQuestions
          : [];
        let nextChecklistData = state.checklistData;

        if (state.checklistData) {
          const checklistResponses = Array.isArray(state.checklistData.checklistResponses)
            ? [...state.checklistData.checklistResponses]
            : [];
          const globalIndex = checklistResponses.findIndex(
            (responseGroup) => responseGroup.scenarioId === 'global'
          );
          const aiQuestions = Array.isArray(state.checklistData.aiChecklistResponses?.questions)
            ? state.checklistData.aiChecklistResponses.questions
            : [];

          if (globalIndex >= 0) {
            const globalResponse = checklistResponses[globalIndex];
            checklistResponses[globalIndex] = {
              ...globalResponse,
              checklistData: {
                ...globalResponse.checklistData,
                currentQuestions: [
                  ...(globalResponse.checklistData?.currentQuestions || []),
                  newQ,
                ],
              },
            };
          } else {
            checklistResponses.push({
              scenarioId: 'global',
              checklistData: {
                currentQuestions: [...aiQuestions, newQ],
                history: [],
              },
            });
          }

          nextChecklistData = {
            ...state.checklistData,
            checklistResponses,
            aiChecklistResponses: {
              ...(state.checklistData.aiChecklistResponses || {}),
              questions: [...aiQuestions, newQ],
            },
          };
        }

        return {
          checklistQuestions: [...currentQuestions, newQ],
          checklistData: nextChecklistData,
        };
      });

      return newQ;
    },

    editQuestion: (questionId, updates = {}) => {
      const existingQuestion = (get().checklistQuestions || []).find(
        (question) => String(question.id) === String(questionId)
      );
      if (!existingQuestion) return null;

      const hasSingularId = Object.prototype.hasOwnProperty.call(updates, 'concerned_measure_id');
      const hasLegacyIds = Object.prototype.hasOwnProperty.call(updates, 'concerned_measure_ids');
      const nextMeasureId = hasSingularId || hasLegacyIds
        ? getQuestionMeasureId(updates)
        : getQuestionMeasureId(existingQuestion);
      if (!nextMeasureId) return null;

      const normalizedUpdates = { ...updates, concerned_measure_id: nextMeasureId };
      delete normalizedUpdates.concerned_measure_ids;
      const associationChanged = nextMeasureId !== getQuestionMeasureId(existingQuestion);
      if (Object.prototype.hasOwnProperty.call(updates, 'concerned_measures')) {
        normalizedUpdates.concerned_measures = Array.isArray(updates.concerned_measures)
          ? updates.concerned_measures.slice(0, 1)
          : updates.concerned_measures
            ? [updates.concerned_measures]
            : [];
      } else if (associationChanged) {
        normalizedUpdates.concerned_measures = [];
      }

      const nextQuestion = normalizeChecklistQuestion({
        ...existingQuestion,
        ...normalizedUpdates,
      }, [], { language: /^en\b/i.test(String(i18n.language || '')) ? 'en' : 'fr' });
      if (!nextQuestion) return null;

      set((state) => {
        const updateQuestion = (question) => {
          if (String(question.id) !== String(questionId)) return question;
          return normalizeChecklistQuestion({ ...question, ...normalizedUpdates }, []) || question;
        };
        const updatedResponses = (state.checklistData?.checklistResponses || []).map((responseGroup) => {
          if (responseGroup.scenarioId !== 'global') return responseGroup;
          return {
            ...responseGroup,
            checklistData: {
              ...responseGroup.checklistData,
              currentQuestions: (responseGroup.checklistData?.currentQuestions || []).map(updateQuestion),
            },
          };
        });
        const updatedResponsibles = (state.checklistData?.responsibleChecklists || []).map((responsible) => {
          const hasQuestion = getSavedResponses(responsible).some(
            (response) => String(response.questionId) === String(questionId)
          );
          return hasQuestion
            ? updateResponsibleResponse(responsible, questionId, {
                question: nextQuestion.question,
                category: nextQuestion.category,
                concerned_measure_id: nextQuestion.concerned_measure_id,
                concerned_measures: nextQuestion.concerned_measures,
              })
            : responsible;
        });

        return {
          checklistQuestions: (state.checklistQuestions || []).map(updateQuestion),
          checklistData: state.checklistData
            ? {
                ...state.checklistData,
                checklistResponses: updatedResponses,
                aiChecklistResponses: state.checklistData.aiChecklistResponses
                  ? {
                      ...state.checklistData.aiChecklistResponses,
                      questions: (state.checklistData.aiChecklistResponses.questions || []).map(updateQuestion),
                    }
                  : state.checklistData.aiChecklistResponses,
                responsibleChecklists: updatedResponsibles,
              }
            : state.checklistData,
        };
      });

      return nextQuestion;
    },

    deleteQuestion: (questionId) => {
      set((state) => {
        const matches = (question) => String(question.id) === String(questionId);
        const updatedResponses = (state.checklistData?.checklistResponses || []).map((responseGroup) => {
          if (responseGroup.scenarioId !== 'global') return responseGroup;
          return {
            ...responseGroup,
            checklistData: {
              ...responseGroup.checklistData,
              currentQuestions: (responseGroup.checklistData?.currentQuestions || []).filter(
                (question) => !matches(question)
              ),
            },
          };
        });
        const updatedResponsibles = (state.checklistData?.responsibleChecklists || []).map((responsible) => {
          const versionData = getCurrentVersionData(responsible);
          const responses = getSavedResponses(responsible);
          const filteredResponses = responses.filter(
            (response) => String(response.questionId) !== String(questionId)
          );
          if (filteredResponses.length === responses.length) return responsible;

          const nextVersionData = {
            ...(versionData || { versionNumber: responsible.currentVersion || 1 }),
            responses: filteredResponses,
          };
          const nextVersions = Array.isArray(responsible.versions)
            ? responsible.versions.map((version) => (
              String(version.versionNumber) === String(nextVersionData.versionNumber)
                ? nextVersionData
                : version
            ))
            : responsible.versions;
          return {
            ...responsible,
            versions: nextVersions,
            currentVersionData: nextVersionData,
          };
        });

        return {
          checklistQuestions: (state.checklistQuestions || []).filter((question) => !matches(question)),
          checklistData: state.checklistData
            ? {
                ...state.checklistData,
                checklistResponses: updatedResponses,
                aiChecklistResponses: state.checklistData.aiChecklistResponses
                  ? {
                      ...state.checklistData.aiChecklistResponses,
                      questions: (state.checklistData.aiChecklistResponses.questions || [])
                        .filter((question) => !matches(question)),
                    }
                  : state.checklistData.aiChecklistResponses,
                responsibleChecklists: updatedResponsibles,
              }
            : state.checklistData,
        };
      });
    }
  }))
);

export default useImplementationChecklistStore;
